<script setup lang="ts">
import {
  pickReleaseNotes,
  releaseLabel,
  type ChangelogChannel,
  type ChangelogFilter,
  type ChangelogPage,
  type ChangelogPlatformId,
  type ChangelogRelease,
  type ChangelogVersion,
} from '~/utils/clientChangelog'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

type ChannelFilter = ChangelogFilter
const PLATFORMS: ChangelogPlatformId[] = ['desktop', 'ios', 'android', 'router']
const FILTERS: ChannelFilter[] = ['all', 'stable', 'beta']
// PandaUnion pages one client and line: five releases first, then ten per "show older".
const FIRST_PAGE = 5
const MORE_PAGE = 10

const queryValue = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? value as T : fallback

// The URL holds the selected client and line, so links and back/forward work.
const platformId = computed(() => queryValue(route.query.platform, PLATFORMS, 'desktop'))
const filter = computed(() => queryValue(route.query.channel, FILTERS, 'all'))

const select = (change: { platform?: ChangelogPlatformId, channel?: ChannelFilter }) => {
  const platform = change.platform ?? platformId.value
  const channel = change.channel ?? filter.value
  router.push({
    query: {
      ...route.query,
      platform: platform === 'desktop' ? undefined : platform,
      channel: channel === 'all' ? undefined : channel,
    },
  })
}

const fetchPage = (platform: ChangelogPlatformId, channel: ChannelFilter, offset: number, limit: number) =>
  useAPI().get<ChangelogPage>('client/changelog', { query: { platform, channel, offset, limit } })

// The first page of the selected client and line, tagged with them, so a page
// of the previous selection is never shown as the new one.
const { data, status, refresh } = useAsyncData(
  'client-changelog',
  async () => {
    const [platform, channel] = [platformId.value, filter.value]
    return { platform, channel, page: await fetchPage(platform, channel, 0, FIRST_PAGE) }
  },
  { server: false, watch: [platformId, filter] },
)
const firstPage = computed(() =>
  status.value !== 'error' && data.value?.platform === platformId.value && data.value.channel === filter.value ? data.value.page : null)
// The current versions and counts belong to the client, so they stay while another line loads.
const clientPage = computed(() => (status.value !== 'error' && data.value?.platform === platformId.value ? data.value.page : null))
const current = computed(() => clientPage.value?.current ?? null)
const countOf = (channel: ChannelFilter) => clientPage.value?.counts[channel] ?? 0

// Older pages of the line on screen, loaded with "show older".
const older = ref<ChangelogRelease[]>([])
const loadingOlder = ref(false)
const olderFailed = ref(false)
watch([platformId, filter], () => {
  older.value = []
  olderFailed.value = false
})
const shown = computed(() => (firstPage.value ? [...firstPage.value.releases, ...older.value] : []))
const hiddenCount = computed(() => (firstPage.value ? countOf(filter.value) - shown.value.length : 0))

async function loadOlder() {
  const [platform, channel] = [platformId.value, filter.value]
  loadingOlder.value = true
  olderFailed.value = false
  try {
    const page = await fetchPage(platform, channel, shown.value.length, MORE_PAGE)
    if (platform !== platformId.value || channel !== filter.value) return
    // A release published since the first page shifts the window; skip what is already shown.
    const seen = new Set(shown.value.map(releaseKey))
    older.value.push(...page.releases.filter(release => !seen.has(releaseKey(release))))
  }
  catch (error) {
    console.error('[changelog] older releases failed', error)
    if (platform === platformId.value && channel === filter.value) olderFailed.value = true
  }
  finally {
    loadingOlder.value = false
  }
}

const sameVersion = (left: ChangelogVersion | null | undefined, right: ChangelogVersion) =>
  Boolean(left) && left!.version === right.version && left!.build === right.build
// Legacy desktop releases can use one version on both lines, or twice on one.
const releaseKey = (release: ChangelogRelease) => `${release.channel}-${release.version}-${release.build}-${release.publishedAt}`

const latestTag = (release: ChangelogRelease) => {
  if (release.channel === 'stable' && sameVersion(current.value?.stable, release)) return t('changelog.latest_stable')
  if (release.channel === 'beta' && sameVersion(current.value?.beta, release)) return t('changelog.latest_beta')
  return ''
}

const intlLocale = computed(() => (locale.value === 'zh' ? 'zh-CN' : 'en-US'))
const dayFormat = computed(() => new Intl.DateTimeFormat(intlLocale.value, { month: 'short', day: 'numeric' }))
const fullFormat = computed(() => new Intl.DateTimeFormat(intlLocale.value, { year: 'numeric', month: 'short', day: 'numeric' }))
const yearOf = (release: ChangelogRelease) => (release.publishedAt ? new Date(release.publishedAt).getFullYear() : null)

// Releases grouped under a year heading, newest first.
const yearGroups = computed(() => {
  const groups: Array<{ year: number | null, releases: ChangelogRelease[] }> = []
  for (const release of shown.value) {
    const year = yearOf(release)
    const last = groups[groups.length - 1]
    if (last && last.year === year) last.releases.push(release)
    else groups.push({ year, releases: [release] })
  }
  return groups
})

// The hero board: where each line stands for the selected client.
const board = computed(() => {
  const { stable, beta } = current.value ?? { stable: null, beta: null }
  const dateText = (version: { publishedAt: string | null } | null) =>
    version?.publishedAt ? fullFormat.value.format(new Date(version.publishedAt)) : ''
  const shape = beta ? 'ahead' : stable ? 'merged' : 'none'
  return {
    shape,
    stable: {
      version: stable ? releaseLabel(stable) : t('changelog.not_released'),
      date: dateText(stable),
      released: Boolean(stable),
    },
    beta: {
      version: beta ? releaseLabel(beta) : stable ? t('changelog.beta_same') : t('changelog.not_released'),
      date: dateText(beta),
      released: Boolean(beta),
    },
  }
})

const boardLabel = computed(() => t('changelog.lines_aria', { stable: board.value.stable.version, beta: board.value.beta.version }))

// Releases without notes are not listed, so an empty line can still have a current version.
const emptyText = computed(() => {
  const { stable, beta } = current.value ?? { stable: null, beta: null }
  if (filter.value === 'beta' && !beta && stable) {
    return t('changelog.empty_beta', { version: releaseLabel(stable) })
  }
  if (filter.value === 'stable' && !stable) return t('changelog.empty_stable')
  return t('changelog.empty')
})

const notesOf = (release: ChangelogRelease) => pickReleaseNotes(release, locale.value)

const pageTitle = computed(() => `${t('changelog.page_title')} · ${t('site_name')}`)
useSeoMeta({
  title: () => pageTitle.value,
  description: () => t('changelog.page_description'),
  ogTitle: () => pageTitle.value,
  ogDescription: () => t('changelog.page_description'),
})
useHead({ title: () => pageTitle.value })
</script>

<template lang="pug">
div.cl-shell(:class="{ 'is-zh': locale === 'zh' }")
  main.cl-main
    .cl-frame
      section.cl-hero
        .cl-hero-copy
          p.cl-eyebrow
            span.cl-eyebrow-line
            span {{ t('changelog.eyebrow') }}
          h1.cl-title {{ t('changelog.title') }}
          p.cl-lead {{ t('changelog.lead') }}
          NuxtLink.cl-download(to="/download")
            span {{ t('changelog.download') }}
            Icon(name="lucide:arrow-up-right" aria-hidden="true")

        figure.cl-board(
          data-testid="changelog-board"
          :class="[`is-${board.shape}`, { 'is-ready': status === 'success' }]"
          role="img"
          :aria-label="boardLabel"
        )
          svg.cl-board-svg(viewBox="0 0 400 200" aria-hidden="true")
            path.cl-board-future(d="M0 140 H400")
            path.cl-board-future.is-beta(v-if="board.shape === 'ahead'" d="M330 60 H400")
            path.cl-board-rail.is-stable(v-if="board.stable.released" pathLength="1" d="M0 140 H230")
            path.cl-board-rail.is-beta(v-if="board.shape === 'ahead'" pathLength="1" d="M60 140 C 110 140, 110 60, 160 60 H 330")
            path.cl-board-rail.is-beta(v-else-if="board.shape === 'merged'" pathLength="1" d="M60 140 C 105 140, 105 60, 150 60 H 160 C 205 60, 205 140, 230 140")
            circle.cl-board-node.is-stable(v-if="board.stable.released" cx="230" cy="140" r="6")
            circle.cl-board-halo(v-if="board.shape === 'ahead'" cx="330" cy="60" r="6")
            circle.cl-board-node.is-beta(v-if="board.shape === 'ahead'" cx="330" cy="60" r="6")
          .cl-board-label.is-beta(data-testid="changelog-board-beta" :class="{ 'is-muted': !board.beta.released }")
            span.cl-board-kicker {{ t('changelog.channels.beta') }}
            span.cl-board-version {{ board.beta.version }}
            span.cl-board-date(v-if="board.beta.date") {{ board.beta.date }}
          .cl-board-label.is-stable(data-testid="changelog-board-stable" :class="{ 'is-muted': !board.stable.released }")
            span.cl-board-kicker {{ t('changelog.channels.stable') }}
            span.cl-board-version {{ board.stable.version }}
            span.cl-board-date(v-if="board.stable.date") {{ board.stable.date }}

      section.cl-controls
        .cl-platforms(role="group" :aria-label="t('changelog.platform_label')")
          button.cl-platform(
            v-for="id in PLATFORMS"
            :key="id"
            type="button"
            :data-testid="`changelog-platform-${id}`"
            :aria-pressed="platformId === id"
            :class="{ 'is-active': platformId === id }"
            @click="select({ platform: id })"
          )
            span.cl-platform-name {{ t(`changelog.platforms.${id}`) }}
            span.cl-platform-hint {{ t(`changelog.platforms.${id}_hint`) }}
        .cl-filters(role="group" :aria-label="t('changelog.channel_label')")
          button.cl-filter(
            v-for="option in FILTERS"
            :key="option"
            type="button"
            :data-testid="`changelog-filter-${option}`"
            :aria-pressed="filter === option"
            :class="[`is-${option}`, { 'is-active': filter === option }]"
            @click="select({ channel: option })"
          )
            i.cl-filter-dot(v-if="option !== 'all'" aria-hidden="true")
            span {{ t(`changelog.channels.${option}`) }}
            span.cl-filter-count(v-if="clientPage") {{ countOf(option) }}

      section.cl-body
        .cl-state(v-if="!firstPage && status !== 'error'" data-testid="changelog-loading" aria-live="polite")
          span.cl-sr-only {{ t('changelog.loading') }}
          .cl-skeleton(v-for="index in 3" :key="index" aria-hidden="true")
            span.cl-skeleton-line.is-title
            span.cl-skeleton-line
            span.cl-skeleton-line.is-short
        .cl-state.is-message(v-else-if="status === 'error'" data-testid="changelog-error" role="alert")
          p {{ t('changelog.error') }}
          button.cl-more(type="button" @click="refresh()") {{ t('changelog.retry') }}
        .cl-state.is-message(v-else-if="!shown.length" data-testid="changelog-empty" role="status")
          p {{ emptyText }}
        template(v-else)
          ol.cl-timeline(:key="`${platformId}-${filter}`" :class="`is-${filter}`" data-testid="changelog-timeline")
            template(v-for="group in yearGroups" :key="group.year ?? 'undated'")
              li.cl-year(v-if="group.year" aria-hidden="true")
                span.cl-year-text {{ group.year }}
              li.cl-release(
                v-for="(release, index) in group.releases"
                :key="releaseKey(release)"
                :class="[`is-${release.channel}`, { 'is-latest': latestTag(release) }]"
                :style="{ '--i': Math.min(index, 8) }"
                :data-channel="release.channel"
                data-testid="changelog-release"
              )
                span.cl-node(aria-hidden="true")
                time.cl-date(v-if="release.publishedAt" :datetime="release.publishedAt") {{ dayFormat.format(new Date(release.publishedAt)) }}
                article.cl-entry
                  header.cl-entry-head
                    h2.cl-version {{ releaseLabel(release) }}
                    span.cl-pill(:class="`is-${release.channel}`") {{ t(`changelog.channels.${release.channel}`) }}
                    span.cl-latest(v-if="latestTag(release)") {{ latestTag(release) }}
                  p.cl-lang-note(v-if="notesOf(release).lang !== locale") {{ t('changelog.notes_in_other_language') }}
                  ChangelogNotes(:text="notesOf(release).text" :lang="notesOf(release).lang")
          .cl-state.is-message(v-if="olderFailed" data-testid="changelog-more-error" role="alert")
            p {{ t('changelog.error') }}
            button.cl-more(type="button" :disabled="loadingOlder" @click="loadOlder") {{ t('changelog.retry') }}
          .cl-more-row(v-else-if="hiddenCount > 0")
            button.cl-more(type="button" data-testid="changelog-more" :disabled="loadingOlder" :aria-busy="loadingOlder" @click="loadOlder")
              span {{ loadingOlder ? t('changelog.loading') : t('changelog.show_older', { count: hiddenCount }) }}
              Icon(:name="loadingOlder ? 'lucide:loader-2' : 'lucide:chevron-down'" :class="{ 'animate-spin': loadingOlder }" aria-hidden="true")
</template>

<style lang="postcss" scoped>
.cl-shell {
  --cl-bg: var(--landing-background);
  --cl-surface-2: #161a15;
  --cl-text: #f5f7f1;
  --cl-copy: #969f92;
  --cl-faint: #626a5e;
  --cl-line: rgba(255, 255, 255, 0.08);
  --cl-line-strong: rgba(255, 255, 255, 0.18);
  --cl-stable: #7ed321;
  --cl-stable-soft: rgba(126, 211, 33, 0.14);
  --cl-beta: #d4b263;
  --cl-beta-soft: rgba(212, 178, 99, 0.14);
  --cl-on-stable: #071006;
  --cl-display: Metropolis, 'SF Pro Display', 'Segoe UI', sans-serif;
  --cl-ease: cubic-bezier(0.16, 1, 0.3, 1);
  /* Timeline lanes: the stable line, then the beta line. */
  --cl-lane-stable: 0.55rem;
  --cl-lane-beta: 1.85rem;
  --cl-gutter: 3.4rem;

  position: relative;
  width: 100%;
  min-width: 100%;
  align-self: stretch;
  /* The page wrapper centers its child; fill it so a short list leaves no band. */
  flex: 1 0 auto;
  overflow: clip;
  background: var(--cl-bg);
  color: var(--cl-text);
}

/* The global p/span/heading rule sets a color; text here inherits instead. */
.cl-shell :where(h1, h2, p, span) {
  color: inherit;
}

.cl-shell::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 46rem;
  pointer-events: none;
  background:
    radial-gradient(56rem 26rem at 88% -8%, rgba(126, 211, 33, 0.08), transparent 64%),
    linear-gradient(rgba(126, 211, 33, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(126, 211, 33, 0.04) 1px, transparent 1px);
  background-size: auto, 48px 48px, 48px 48px;
  mask-image: linear-gradient(180deg, #000 0%, #000 40%, transparent 100%);
}

.light .cl-shell {
  --cl-surface-2: #e8edf1;
  --cl-text: #1e293b;
  --cl-copy: #55657a;
  --cl-faint: #8d9bb0;
  --cl-line: rgba(51, 65, 85, 0.12);
  --cl-line-strong: rgba(51, 65, 85, 0.26);
  --cl-stable: #2d5a27;
  --cl-stable-soft: rgba(45, 90, 39, 0.1);
  --cl-beta: #96711a;
  --cl-beta-soft: rgba(150, 113, 26, 0.12);
  --cl-on-stable: #f6fff0;
}

.light .cl-shell::before {
  background:
    radial-gradient(56rem 26rem at 88% -8%, rgba(45, 90, 39, 0.07), transparent 64%),
    linear-gradient(rgba(51, 65, 85, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(51, 65, 85, 0.06) 1px, transparent 1px);
  background-size: auto, 40px 40px, 40px 40px;
}

.cl-main {
  position: relative;
  z-index: 1;
  padding: 0 1.5rem clamp(4rem, 8vw, 7rem);
}

.cl-frame {
  width: min(100%, 1120px);
  margin-inline: auto;
}

.cl-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* ---------- hero ---------- */

.cl-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  align-items: center;
  gap: clamp(2rem, 5vw, 5rem);
  padding-block: clamp(3.5rem, 7vw, 6rem) clamp(2.5rem, 5vw, 3.75rem);
}

.cl-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--cl-copy);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
}

.cl-eyebrow-line {
  width: 2.6rem;
  height: 1px;
  background: var(--cl-stable);
  opacity: 0.6;
}

.cl-title {
  display: grid;
  margin-top: 1.5rem;
  font-family: var(--cl-display);
  font-size: clamp(2.6rem, 4.6vw, 4.4rem);
  font-weight: 700;
  line-height: 0.98;
  letter-spacing: -0.05em;
  text-wrap: balance;
}

.cl-lead {
  max-width: 31rem;
  margin-top: 1.5rem;
  color: var(--cl-copy);
  font-size: clamp(1rem, 1.25vw, 1.1rem);
  line-height: 1.72;
}

.cl-download {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1.9rem;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid var(--cl-line-strong);
  color: var(--cl-text);
  font-size: 0.92rem;
  font-weight: 600;
  transition: border-color 0.3s var(--cl-ease), gap 0.3s var(--cl-ease);
}

.cl-download:hover {
  gap: 0.6rem;
  border-color: var(--cl-stable);
}

.cl-shell.is-zh .cl-title {
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Source Han Sans SC', 'Microsoft YaHei', sans-serif;
  font-size: clamp(2.4rem, 4vw, 3.6rem);
  line-height: 1.16;
  letter-spacing: normal;
}

.cl-shell.is-zh .cl-lead {
  line-height: 1.85;
}

/* The two release lines for the selected client. */
.cl-board {
  position: relative;
  aspect-ratio: 2 / 1;
  margin: 0;
}

.cl-board-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.cl-board-future {
  fill: none;
  stroke: var(--cl-line-strong);
  stroke-width: 1.5;
  stroke-dasharray: 2 7;
  stroke-linecap: round;
}

.cl-board-rail {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
}

.cl-board-rail.is-stable {
  stroke: var(--cl-stable);
}

.cl-board-rail.is-beta {
  stroke: var(--cl-beta);
}

.cl-board.is-ready .cl-board-rail {
  animation: cl-draw 1.4s var(--cl-ease) forwards;
}

.cl-board.is-ready .cl-board-rail.is-beta {
  animation-delay: 0.25s;
}

.cl-board-node {
  stroke-width: 3;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}

.cl-board-node.is-stable {
  fill: var(--cl-stable);
  stroke: var(--cl-bg);
}

.cl-board-node.is-beta {
  fill: var(--cl-bg);
  stroke: var(--cl-beta);
}

.cl-board.is-ready .cl-board-node {
  animation: cl-pop 0.5s var(--cl-ease) 0.9s forwards;
}

.cl-board-halo {
  fill: var(--cl-beta-soft);
  stroke: none;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}

.cl-board.is-ready .cl-board-halo {
  animation: cl-halo 2.8s ease-out 1.4s infinite;
}

.cl-board-label {
  position: absolute;
  display: grid;
  justify-items: end;
  gap: 0.25rem;
  text-align: right;
  transform: translateX(-100%);
  opacity: 0;
  transition: opacity 0.6s var(--cl-ease) 0.7s;
}

.cl-board.is-ready .cl-board-label {
  opacity: 1;
}

/* Node x / 400 and y / 200 of the SVG. Labels end 12px left of the node. */
.cl-board-label.is-beta {
  left: calc(82.5% - 0.9rem);
  bottom: calc(70% + 0.9rem);
}

.cl-board.is-merged .cl-board-label.is-beta,
.cl-board.is-none .cl-board-label.is-beta {
  left: calc(48% + 0.5rem);
  transform: translateX(-50%);
  justify-items: center;
  text-align: center;
}

.cl-board-label.is-stable {
  left: calc(57.5% - 0.9rem);
  top: calc(70% + 0.9rem);
}

.cl-board-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--cl-faint);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cl-board-label.is-stable .cl-board-kicker {
  color: var(--cl-stable);
}

.cl-board-label.is-beta .cl-board-kicker {
  color: var(--cl-beta);
}

.cl-board-version {
  font-family: var(--cl-display);
  font-size: clamp(1.25rem, 2.2vw, 1.7rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.cl-board-label.is-muted .cl-board-version {
  color: var(--cl-faint);
  font-size: clamp(1rem, 1.6vw, 1.15rem);
  letter-spacing: normal;
}

.cl-board-date {
  color: var(--cl-faint);
  font-size: 0.8rem;
  white-space: nowrap;
}

/* ---------- controls ---------- */

.cl-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem 2rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--cl-line);
}

.cl-platforms {
  display: flex;
  gap: clamp(1.25rem, 3vw, 2.5rem);
}

.cl-platform {
  position: relative;
  display: grid;
  justify-items: start;
  gap: 0.3rem;
  padding: 0.2rem 0 0.35rem;
  color: var(--cl-faint);
  text-align: left;
  transition: color 0.25s ease;
}

.cl-platform::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(-1.25rem - 1px);
  height: 2px;
  background: var(--cl-text);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s var(--cl-ease);
}

.cl-platform:hover,
.cl-platform.is-active {
  color: var(--cl-text);
}

.cl-platform.is-active::after {
  transform: scaleX(1);
}

.cl-platform-name {
  font-family: var(--cl-display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
}

.cl-platform-hint {
  color: var(--cl-faint);
  font-size: 0.74rem;
  line-height: 1;
  white-space: nowrap;
}

.cl-filters {
  display: inline-flex;
  gap: 0.2rem;
  padding: 0.2rem;
  border: 1px solid var(--cl-line);
  border-radius: 999px;
}

.cl-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  color: var(--cl-copy);
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
  transition: background-color 0.25s ease, color 0.25s ease;
}

.cl-filter:hover {
  color: var(--cl-text);
}

.cl-filter.is-active {
  background: var(--cl-surface-2);
  color: var(--cl-text);
}

.cl-filter-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
}

.cl-filter.is-stable .cl-filter-dot {
  background: var(--cl-stable);
}

.cl-filter.is-beta .cl-filter-dot {
  border: 1.5px solid var(--cl-beta);
}

.cl-filter-count {
  color: var(--cl-faint);
  font-size: 0.74rem;
  font-variant-numeric: tabular-nums;
}

/* ---------- timeline ---------- */

.cl-body {
  padding-top: clamp(1.75rem, 3vw, 2.5rem);
}

.cl-timeline {
  position: relative;
}

/* The two lines run the whole list; each release sits on its own line. */
.cl-timeline::before,
.cl-timeline::after {
  content: '';
  position: absolute;
  top: 0.4rem;
  bottom: 0;
  width: 2px;
  border-radius: 2px;
  mask-image: linear-gradient(180deg, #000 0%, #000 85%, transparent 100%);
}

.cl-timeline::before {
  left: calc(var(--cl-lane-stable) - 1px);
  background: var(--cl-stable);
  opacity: 0.55;
}

.cl-timeline::after {
  left: calc(var(--cl-lane-beta) - 1px);
  background: repeating-linear-gradient(180deg, var(--cl-beta) 0 6px, transparent 6px 11px);
  opacity: 0.6;
}

.cl-timeline.is-stable::after,
.cl-timeline.is-beta::before {
  display: none;
}

.cl-year {
  position: relative;
  padding: 1.6rem 0 1.1rem var(--cl-gutter);
}

.cl-year:first-child {
  padding-top: 0;
}

.cl-year-text {
  font-family: var(--cl-display);
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  font-weight: 700;
  letter-spacing: -0.05em;
  line-height: 1;
  color: transparent;
  -webkit-text-stroke: 1px var(--cl-line-strong);
}

.cl-release {
  position: relative;
  display: grid;
  grid-template-columns: 7.5rem minmax(0, 1fr);
  gap: 0 clamp(1rem, 3vw, 2.5rem);
  padding: 0 0 clamp(2.25rem, 4vw, 3rem) var(--cl-gutter);
  opacity: 0;
  transform: translateY(10px);
  animation: cl-rise 0.7s var(--cl-ease) forwards;
  animation-delay: calc(var(--i, 0) * 45ms);
}

.cl-node {
  position: absolute;
  top: 0.42rem;
  left: calc(var(--cl-lane-stable) - 0.45rem);
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 999px;
  background: var(--cl-stable);
  box-shadow: 0 0 0 4px var(--cl-bg);
}

.cl-release.is-beta .cl-node {
  left: calc(var(--cl-lane-beta) - 0.4rem);
  width: 0.8rem;
  height: 0.8rem;
  top: 0.47rem;
  border: 2px solid var(--cl-beta);
  background: var(--cl-bg);
}

.cl-release.is-latest .cl-node {
  box-shadow: 0 0 0 4px var(--cl-bg), 0 0 0 9px var(--cl-stable-soft);
}

.cl-release.is-latest.is-beta .cl-node {
  box-shadow: 0 0 0 4px var(--cl-bg), 0 0 0 9px var(--cl-beta-soft);
}

.cl-date {
  padding-top: 0.3rem;
  color: var(--cl-copy);
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.cl-entry {
  grid-column: 2;
  min-width: 0;
  max-width: 46rem;
}

.cl-entry-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.7rem;
  margin-bottom: 0.85rem;
}

.cl-version {
  font-family: var(--cl-display);
  font-size: clamp(1.35rem, 2vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.cl-pill {
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
}

.cl-pill.is-stable {
  background: var(--cl-stable-soft);
  color: var(--cl-stable);
}

.cl-pill.is-beta {
  background: var(--cl-beta-soft);
  color: var(--cl-beta);
}

.cl-latest {
  color: var(--cl-faint);
  font-size: 0.78rem;
  font-weight: 600;
}

.cl-latest::before {
  content: '·';
  margin-right: 0.55rem;
}

.cl-lang-note {
  margin-bottom: 0.5rem;
  color: var(--cl-faint);
  font-size: 0.76rem;
}

.cl-more-row {
  padding-left: var(--cl-gutter);
}

.cl-more {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.6rem;
  padding: 0.7rem 1.15rem;
  border: 1px solid var(--cl-line-strong);
  border-radius: 999px;
  color: var(--cl-text);
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1;
  transition: border-color 0.25s ease, background-color 0.25s ease;
}

.cl-more:hover {
  border-color: var(--cl-text);
  background: var(--cl-surface-2);
}

.cl-state {
  display: grid;
  gap: 2.5rem;
  padding-left: var(--cl-gutter);
}

.cl-state.is-message {
  justify-items: start;
  gap: 1rem;
  padding-block: 1rem 3rem;
  color: var(--cl-copy);
  font-size: 0.98rem;
  line-height: 1.7;
}

.cl-skeleton {
  display: grid;
  gap: 0.7rem;
  max-width: 34rem;
}

.cl-skeleton-line {
  height: 0.8rem;
  border-radius: 999px;
  background: var(--cl-surface-2);
  animation: cl-shimmer 1.4s ease-in-out infinite alternate;
}

.cl-skeleton-line.is-title {
  width: 9rem;
  height: 1.4rem;
}

.cl-skeleton-line.is-short {
  width: 60%;
}

/* ---------- motion ---------- */

@keyframes cl-draw {
  to { stroke-dashoffset: 0; }
}

@keyframes cl-pop {
  from { opacity: 0; transform: scale(0.4); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes cl-halo {
  0% { opacity: 0.9; transform: scale(1); }
  100% { opacity: 0; transform: scale(3.2); }
}

@keyframes cl-rise {
  to { opacity: 1; transform: none; }
}

@keyframes cl-shimmer {
  from { opacity: 0.45; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .cl-board-rail,
  .cl-board-node,
  .cl-board-label,
  .cl-release {
    animation: none !important;
    transition: none;
    opacity: 1;
    stroke-dashoffset: 0;
    transform: none;
  }

  .cl-board-label {
    transform: translateX(-100%);
  }

  .cl-board.is-merged .cl-board-label.is-beta,
  .cl-board.is-none .cl-board-label.is-beta {
    transform: translateX(-50%);
  }

  .cl-board-halo {
    display: none;
  }

  .cl-platform::after {
    transition: none;
  }
}

/* ---------- small screens ---------- */

@media (max-width: 900px) {
  .cl-hero {
    grid-template-columns: minmax(0, 1fr);
  }

  .cl-board {
    width: min(100%, 30rem);
    /* The stable label hangs below the board. */
    margin-bottom: 4.5rem;
  }
}

@media (max-width: 640px) {
  .cl-shell {
    --cl-lane-stable: 0.45rem;
    --cl-lane-beta: 1.35rem;
    --cl-gutter: 2.4rem;
  }

  .cl-main {
    padding-inline: 1rem;
  }

  .cl-controls {
    align-items: stretch;
    flex-direction: column;
  }

  /* Four clients with hints do not fit in one row: two columns, underline under the name. */
  .cl-platforms {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem 1.5rem;
  }

  .cl-platform::after {
    bottom: -0.2rem;
    right: auto;
    width: 1.5rem;
  }

  .cl-filters {
    align-self: flex-start;
    margin-top: 0.5rem;
  }

  .cl-release {
    grid-template-columns: minmax(0, 1fr);
  }

  .cl-date {
    padding: 0 0 0.35rem;
    font-size: 0.78rem;
  }

  .cl-entry {
    grid-column: 1;
  }

  .cl-node {
    top: 0.12rem;
  }

  .cl-release.is-beta .cl-node {
    top: 0.17rem;
  }
}
</style>
